import React from 'react';
import { i18nFn, useAppSettings, useAuthPin, useCreateMemberPassword, useSpringBigStore } from '@springbig/shared';
import { VStack, HStack, Pressable, FormControl, Input, WarningIcon } from 'native-base';
import { Caption, H3 } from '~/shared/Typography';
import Button from '~/shared/Button';
import { useFormStyles, useGeneralStyles } from '~/shared/styles';
import * as yup from 'yup';
import { useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AppRouteProps, useAppNavigate } from '~/pages';
import { NavigateOptions, Params } from 'react-router';

type CreateStepProps = {
    t: i18nFn;
    onEvent: (message: string) => void;
    navigate: (route: AppRouteProps, opts?: NavigateOptions) => void;
    params: Params<string>;
};

export const CreateStep = ({
    t,
    onEvent,
    navigate, 
    params,
}: CreateStepProps) => {


    if (!navigate) {
        navigate = useAppNavigate().navigate;
    }

    if (!params) {
        params = useAppNavigate().params;
    }

    const { defaultScreen } = useAppSettings();
    const gStyle = useGeneralStyles();
    const {
        input_background,
        input_border,
        input_label,
        input_text
    } = useFormStyles();

    const { mutate: createPassword, isLoading: isLoadingCode } = useCreateMemberPassword();
    const { mutate: authPin } = useAuthPin();

    const codeSize = 4;

    const schemaCode = yup.object().shape({
        code: yup
            .string()
            .required('code is required')
            .matches(new RegExp(`^.{${codeSize}}$`), 'invalid code format')
            .trim(),
        confirm_code: yup
            .string()
            .required('confirming code is required')
            .matches(new RegExp(`^.{${codeSize}}$`), 'invalid code format')
            .oneOf([yup.ref('code'), null], 'codes must match')
            .trim()
    });

    type FormDataCode = yup.InferType<typeof schemaCode>;

    const { control: controlCode, handleSubmit: handleSubmitCode, formState: { errors: errorsCode }, watch } = useForm({
        resolver: yupResolver(schemaCode)
    });

    const { field: codeField } = useController({
        control: controlCode,
        defaultValue: '',
        name: 'code',
    });

    const { field: confirmCodeField } = useController({
        control: controlCode,
        defaultValue: '',
        name: 'confirm_code',
    });
    const { setMobileSubscriberId, setWalletId, setPhoneNumber, stashboardTarget } = useSpringBigStore();

    const onSubmitCode = (data: FormDataCode): void => {

        createPassword({
            walletId: params.wallet_id,
            member: {
                password: data.code,
            }
        }, {
            onSuccess: (_) => {

                let target;
                // const messageTarget = stashboardTarget ? `messages/${stashboardTarget}` : defaultScreen;
                // let target = `w/${params.wallet_id}/${(messageTarget || 'messages')}`;

                const plan_id = localStorage.getItem('plan_id');
                if (plan_id) {
                    localStorage.removeItem('plan_id');
                    target = `w/${params.wallet_id}/subscription/${plan_id}`;
                }

                const link_id = localStorage.getItem('link_id');
                if (link_id) {
                    localStorage.removeItem('link_id');
                    target = `w/${params.wallet_id}/subscriptions`;
                }

                authPin({
                    pin: btoa(data.code),
                    stashboardOrMemberKey: params.wallet_id,
                    reference: 'member'
                }, {
                    onSuccess: (response) => {

                        if (response) {
                            setWalletId(response.merchant_member_key);
                            setPhoneNumber(response.phone_number);
                            setMobileSubscriberId(response.mobile_subscriber);
                            if (target) {
                                navigate(target as AppRouteProps);
                            }
                            else {
                                onEvent('welcome');
                            }
                        }
                    }
                });
            },
        });
    };

    const codeValue = watch('code');
    const confirmCodeValue = watch('confirm_code');

    return (
      <VStack backgroundColor={gStyle.screen_background} space={8} maxWidth={64} alignItems={'center'}>
          <VStack>
              <H3 fontSize={20}>
                  {t('ten_dlc.create.title')}
              </H3>
              <Caption>
                  {t('ten_dlc.create.description')}
              </Caption>

              <FormControl isInvalid={!!errorsCode.code}>
                  <VStack>
                      <VStack>
                          <FormControl.Label color={input_label}>
                              { t('auth.ten_dlc.create.create_pin') }
                          </FormControl.Label>
                          <Input
                              {...codeField}
                              backgroundColor={input_background}
                              borderColor={input_border}
                              height={10}
                              fontSize={16}
                              color={input_text}
                              isInvalid={!!errorsCode.code}
                              isDisabled={isLoadingCode}
                              maxLength={codeSize}
                              returnKeyType='done'
                          />
                      </VStack>
                      <VStack>
                          <FormControl.Label color={input_label}>
                              { t('auth.ten_dlc.create.confirm_pin') }
                          </FormControl.Label>
                          <Input
                              {...confirmCodeField}
                              backgroundColor={input_background}
                              borderColor={input_border}
                              height={10}
                              fontSize={16}
                              color={input_text}
                              isInvalid={!!errorsCode.code}
                              isDisabled={isLoadingCode}
                              maxLength={codeSize}
                              returnKeyType='done'
                          />
                      </VStack>
                      { errorsCode && errorsCode.code &&
                          <FormControl.ErrorMessage
                              leftIcon={<WarningIcon size={3} />}
                          >
                              { errorsCode.code?.message }
                          </FormControl.ErrorMessage>
                      }
                      { errorsCode && errorsCode.confirm_code &&
                          <FormControl.ErrorMessage
                              leftIcon={<WarningIcon size={3} />}
                          >
                              { errorsCode.confirm_code?.message }
                          </FormControl.ErrorMessage>
                      }
                      { (codeValue && codeValue.length === codeSize && confirmCodeValue && confirmCodeValue.length === codeSize && codeValue !== confirmCodeValue) && (
                          <HStack alignItems={'center'} space={2}>
                              <WarningIcon size={3} color={gStyle.button_danger_background} />
                              <Caption color={gStyle.button_danger_background} fontSize={12}>
                                {t('ten_dlc.create.errors.codesMustMatch')}
                              </Caption>
                          </HStack>
                      )}
                  </VStack>
              </FormControl>
              <Button
                  bg={gStyle.button_background}
                  _text={{ color: gStyle.button_text }}
                  onPress={handleSubmitCode(onSubmitCode)}
                  isLoading={isLoadingCode}
                  mt={4}
              >
                  { t('auth.ten_dlc.create.submit_pin') }
              </Button>
          </VStack>
      </VStack>
  );
};
